// Interface commune aux deux modes : démonstration (dans le navigateur) et cloud (Supabase).
import type {
  CatalogItem, CatalogKind, DeviceAction, DeviceState, Device, Incident, Invitation, Job, Machine, Member, Operator, Organization,
  Role, Task, TaskEvent, TaskWithJob, DocKind, DocPage, JobDocument,
} from "./types.ts";

export interface Session { user_id: string; email: string }

export interface PlanSlot {
  task_id: string;
  machine_id: string | null;
  planned_start: string | null;
  planned_end: string | null;
  setup_min: number | null;
  run_min: number | null;
  locked?: boolean;
  /** Conducteur attribué (absent = inchangé). */
  operator_id?: string | null;
  /** Durée de production forcée (absent = inchangée, null = recalculée). */
  manual_run_min?: number | null;
}

export type NewTask = Pick<Task, "operation" | "fold_type" | "format_in" | "format_out" | "paper" | "grammage" | "pages" | "params" | "packaging"> & { id?: string };
export type JobInput = Pick<Job, "of_number" | "client" | "title" | "quantity" | "due_at" | "priority" | "sheets_ready_at" | "notes">;

/** Toutes les données de l'atelier, sans limite de date (export Excel / CSV). */
export interface ExportData {
  jobs: Job[];
  tasks: Task[];
  incidents: Incident[];
  events: TaskEvent[];
  machines: Machine[];
  operators: Operator[];
}

export interface Api {
  readonly mode: "demo" | "cloud";

  // --- compte -------------------------------------------------------------
  session(): Session | null;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, name: string): Promise<"connecte" | "confirmer">;
  resetPassword(email: string): Promise<void>;
  /** Après un lien « mot de passe oublié » : enregistre le nouveau mot de passe. */
  updatePassword(password: string): Promise<void>;
  /** Message laissé par un lien reçu par mail (lien expiré…), lu une seule fois. */
  takeLinkNotice(): { kind: "recovery" | "confirmed" | "error"; message: string } | null;
  signOut(): Promise<void>;
  /** Imprimerie de l'utilisateur (null s'il n'en a pas encore). */
  loadOrg(): Promise<Organization | null>;
  createOrg(name: string, siret: string, userName: string): Promise<Organization>;
  updateOrg(patch: Partial<Pick<Organization, "name" | "siret" | "hours" | "permissions">>): Promise<void>;
  members(): Promise<Member[]>;
  updateMember(userId: string, role: Role): Promise<void>;
  removeMember(userId: string): Promise<void>;
  invitations(): Promise<Invitation[]>;
  invite(email: string, role: Role): Promise<void>;
  cancelInvite(email: string): Promise<void>;

  // --- listes de l'imprimerie (conditionnements, aléas) ---------------------
  catalog(kind?: CatalogKind): Promise<CatalogItem[]>;
  /** Remplace une liste entière (ordre compris). */
  replaceCatalog(kind: CatalogKind, scope: string, machineId: string | null, labels: string[]): Promise<void>;

  // --- atelier ------------------------------------------------------------
  machines(): Promise<Machine[]>;
  saveMachine(m: Partial<Machine> & { name: string }): Promise<Machine>;
  deleteMachine(id: string): Promise<void>;
  operators(): Promise<Operator[]>;
  saveOperator(o: Partial<Operator> & { name: string }, pin?: string): Promise<Operator>;
  deleteOperator(id: string): Promise<void>;

  // --- jobs ---------------------------------------------------------------
  jobs(): Promise<Job[]>;
  job(id: string): Promise<{ job: Job; tasks: Task[] } | null>;
  saveJob(id: string | null, input: JobInput, tasks: NewTask[]): Promise<Job>;
  cancelJob(id: string): Promise<void>;
  deleteJob(id: string): Promise<void>;

  // --- documents du job (BAT, maquette, photo) ------------------------------
  documents(jobId: string): Promise<JobDocument[]>;
  documentPage(docId: string, page: number): Promise<string>;
  addDocument(jobId: string, doc: { name: string; kind: DocKind; thumb: string; pages: DocPage[] }): Promise<JobDocument>;
  updateDocument(id: string, patch: Partial<Pick<JobDocument, "name" | "kind">>): Promise<void>;
  deleteDocument(id: string): Promise<void>;

  // --- planning -----------------------------------------------------------
  /** Étapes ouvertes et celles terminées depuis `since`. */
  tasks(since?: Date): Promise<TaskWithJob[]>;
  savePlan(slots: PlanSlot[]): Promise<void>;
  publishPlan(): Promise<number>;

  // --- suivi --------------------------------------------------------------
  incidents(since: Date): Promise<Incident[]>;
  events(since: Date): Promise<TaskEvent[]>;
  /** Tout l'historique de l'imprimerie : jobs, étapes, aléas, pointages. */
  exportData(): Promise<ExportData>;

  // --- tablettes ----------------------------------------------------------
  devices(): Promise<Device[]>;
  createPairingCode(machineId: string): Promise<string>;
  deleteDevice(id: string): Promise<void>;
  pairDevice(code: string, name: string): Promise<string>;
  deviceState(token: string): Promise<DeviceState>;
  deviceAction(token: string, operatorId: string, pin: string, action: DeviceAction | { type: "login" }): Promise<DeviceState>;
  deviceDocuments(token: string, jobId: string): Promise<JobDocument[]>;
  deviceDocumentPage(token: string, docId: string, page: number): Promise<string>;

  /** Prévient l'interface qu'il faut relire les données. */
  subscribe(fn: () => void): () => void;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status = 400) { super(message); this.status = status; }
}

export function uid(): string {
  return crypto.randomUUID();
}
